import { useEffect } from 'react';

interface AppManifestInfo {
  name: string;
  /** Absolute or root-relative URL of the app's icon. */
  iconUrl: string;
  /** URL (path + query) that deep-links straight back into this app. */
  startUrl: string;
}

/**
 * Swaps the document's web-app manifest for a per-app one while an app is
 * open, so "Add to Home Screen" / "Install app" installs THIS app — with its
 * own name, icon and a start URL that deep-links straight back into it —
 * rather than the generic Dream shell. Restores the site manifest when the
 * app closes.
 */
export const useAppPwaManifest = (app: AppManifestInfo | null): void => {
  const name = app ? app.name : null;
  const iconUrl = app ? app.iconUrl : null;
  const startUrl = app ? app.startUrl : null;

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]',
    );
    if (!link || !name || !iconUrl || !startUrl) return;

    const originalHref = link.href;
    const origin = window.location.origin;
    const manifest = {
      name,
      short_name: name,
      start_url: new URL(startUrl, origin).href,
      scope: `${origin}/`,
      display: 'standalone',
      background_color: '#FFFEFC',
      theme_color: '#FFFEFC',
      icons: [
        {
          src: new URL(iconUrl, origin).href,
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any',
        },
      ],
    };
    link.href = `data:application/manifest+json,${encodeURIComponent(
      JSON.stringify(manifest),
    )}`;

    return () => {
      link.href = originalHref;
    };
  }, [name, iconUrl, startUrl]);
};
